import {BlueButton} from '@/components/Button/BlueButton';
import {ClientSearch} from '@/components/ClientSearch/ClientSearch';
import {ModaleLayout} from '@/components/Modale/ModaleLayout';
import {ImageIcon} from '@/components/svg/ImageIcon';
import {useApi} from '@/haligator-factory';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {AttachmentIcon} from '@chakra-ui/icons';
import {
  Box,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Select,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {ConversationFichiersPreview} from './ConversationFichiersPreview';
import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {useCommand, useHalCollection, useHalState} from '@synako/haligator';
import {Navigable, State} from '@synako/halitrek';
import {
  ClientDesc,
  ConversationInitieeEventDesc,
} from 'api-types/ffconnect-hal';
import {DOCUMENTS_ACCEPT, IMAGES_ACCEPT, MAX_FILE_SIZE} from 'ff-common';
import {ChangeEvent, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

/**
 * Modale pour initier une nouvelle conversation dans le canal collaboratif.
 */

export const ModaleInitierNouvelleConversation = NiceModal.create<{
  // Reçoit l'event d'initiation pour permettre à l'appelant (ex: popin) d'ouvrir la conversation créée.
  onConversationCreated?: (
    eventState: State<ConversationInitieeEventDesc>,
  ) => void;
  // Client pré-sélectionné (popin fiche client / mode commercial) : masque la recherche client
  // et cible directement ce client. Sans ces props, comportement BO classique (recherche client).
  preselectedClientNav?: Navigable<ClientDesc>;
  preselectedClientId?: string;
}>(({onConversationCreated, preselectedClientNav, preselectedClientId}) => {
  const modal = useModal();
  const {t} = useTranslation();
  const api = useApi();

  // Récupération de l'id de la société commerciale
  const {id_societe_commerciale} = useParams<{
    id_societe_commerciale: string;
  }>();

  // State pour le client sélectionné (initialisé au client pré-sélectionné si fourni)
  const [selectedClientNav, setSelectedClientNav] = useState<
    Navigable<ClientDesc> | undefined
  >(preselectedClientNav);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(
    preselectedClientId,
  );

  // State pour le contact sélectionné
  const [selectedContactId, setSelectedContactId] = useState<
    string | undefined
  >(undefined);

  // State pour le message
  const [messageContent, setMessageContent] = useState('');

  // State pour les pièces jointes
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // Références pour les inputs fichier
  const documentInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Toast pour les notifications
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);

  // Récupération de la société commerciale (uniquement pour la recherche client ; inutile quand
  // le client est déjà pré-sélectionné via la popin).
  const societeCommercialeNav = useMemo(
    () =>
      preselectedClientNav
        ? undefined
        : api.follow('get-societe_commerciale', {
            societe_commerciale_id: id_societe_commerciale,
          }),
    [api, id_societe_commerciale, preselectedClientNav],
  );

  const societeCommercialeHook = useHalState(societeCommercialeNav);

  // Commande pour initier la conversation
  const {submit: submitInitierConversation, isSubmitting} = useCommand(
    selectedClientNav,
    'c:initier-conversation',
    {
      onSuccess: (eventState) => {
        onConversationCreated?.(eventState);
        modal.remove();
      },
      onError,
    },
  );

  // Récupération des contacts du client sélectionné
  const contactsCollection = useHalCollection(
    selectedClientNav?.follow('contacts'),
    {disable: !selectedClientNav},
  );

  // Reset contact quand on change de client
  useEffect(() => {
    setSelectedContactId(undefined);
  }, [selectedClientId]);

  const contactItems = contactsCollection.items ?? [];
  const isSocieteCommercialeLoading = societeCommercialeHook.loading;

  // Nouvelle conversation =>
  // - Client connu via popin fiche client / mode commercial : raison sociale du client
  // - Conversation BO : message générique "Personne à contacter" (avant sélection du client)
  const selectedClientHook = useHalState(selectedClientNav);
  const raisonSociale = selectedClientHook.data?.tolede.raison_sociale;
  const contactLabel = raisonSociale
    ? t('back_office.conversations.personne_a_contacter_chez', {
        raison_sociale: raisonSociale,
      })
    : t('back_office.conversations.personne_a_contacter');

  // Gérer la sélection de fichiers
  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const piecesJointesValides: File[] = [];
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        textToast(t('global.fichier_trop_volumineux', {nom: file.name}));
      } else {
        piecesJointesValides.push(file);
      }
    }
    if (piecesJointesValides.length > 0) {
      setSelectedFiles((prev) => [...prev, ...piecesJointesValides]);
    }
    // Réinitialiser l'input pour permettre de resélectionner le même fichier
    e.target.value = '';
  };

  // Supprimer un fichier de la liste
  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Validation du formulaire
  const isFormValid =
    selectedClientId && selectedContactId && messageContent.trim().length > 0;

  // Corps de la modale
  const body = (
    <VStack
      spacing={4}
      align='stretch'
    >
      {/* Recherche d'un client (masquée quand le client est déjà connu via la popin) */}
      {!preselectedClientNav && societeCommercialeNav && (
        <ClientSearch
          societeCommercialeNav={societeCommercialeNav}
          selectedClientId={selectedClientId}
          onSelectClient={(clientId, clientNav) => {
            setSelectedClientId(clientId);
            setSelectedClientNav(clientNav);
          }}
          onClearSelection={() => {
            setSelectedClientId(undefined);
            setSelectedClientNav(undefined);
            setSelectedContactId(undefined);
          }}
          label={t('back_office.conversations.client')}
          isDisabled={isSocieteCommercialeLoading || !id_societe_commerciale}
        />
      )}

      {/* Sélection du contact */}
      <FormControl
        isRequired
        isDisabled={!selectedClientId}
      >
        <FormLabel>{contactLabel}</FormLabel>
        <Select
          placeholder={t('back_office.conversations.selectionner_contact')}
          value={selectedContactId ?? ''}
          onChange={(e) => {
            setSelectedContactId(e.target.value || undefined);
          }}
          isDisabled={!selectedClientId || contactsCollection.loading}
        >
          {contactItems.map((contactState) => {
            const contact = contactState.data;
            const displayName =
              [contact.tolede.prenom, contact.tolede.nom]
                .filter(Boolean)
                .join(' ') || contact.id;
            return (
              <option
                key={contact.id}
                value={contact.id}
              >
                {displayName}
              </option>
            );
          })}
        </Select>
        {selectedClientId && contactsCollection.loading && (
          <HStack mt={2}>
            <Spinner size='xs' />
            <Text
              fontSize='sm'
              color='gray.500'
            >
              {t('global.loading')}
            </Text>
          </HStack>
        )}
        {/* N'afficher le message que si la collection a été chargée (items défini) */}
        {selectedClientId &&
          !contactsCollection.loading &&
          contactsCollection.items !== undefined &&
          contactItems.length === 0 && (
            <Text
              fontSize='sm'
              color='orange.500'
              mt={1}
            >
              {t('back_office.conversations.aucun_contact_disponible')}
            </Text>
          )}
      </FormControl>

      {/* Message */}
      <FormControl
        isRequired
        isDisabled={!selectedContactId}
      >
        <FormLabel>{t('back_office.conversations.message')}</FormLabel>
        <Textarea
          placeholder={t('back_office.conversations.message_placeholder')}
          value={messageContent}
          onChange={(e) => {
            setMessageContent(e.target.value);
          }}
          rows={4}
          fontSize='sm'
        />
      </FormControl>

      {/* Pièce jointe */}
      <FormControl isDisabled={!selectedContactId}>
        <FormLabel>{t('back_office.conversations.piece_jointe')}</FormLabel>

        {/* Inputs cachés pour sélection de fichiers */}
        <input
          type='file'
          ref={documentInputRef}
          onChange={handleFileSelect}
          accept={DOCUMENTS_ACCEPT}
          multiple // Permet la sélection de plusieurs fichiers à la fois
          style={{display: 'none'}}
        />
        <input
          type='file'
          ref={imageInputRef}
          onChange={handleFileSelect}
          accept={IMAGES_ACCEPT}
          multiple
          style={{display: 'none'}}
        />

        {/* Aperçu des fichiers sélectionnés */}
        <ConversationFichiersPreview
          selectedFiles={selectedFiles}
          onRemoveFile={handleRemoveFile}
        />

        {/* Boutons d'ajout de fichier */}
        <HStack spacing={2}>
          <Tooltip
            label={t('back_office.conversations.ajouter_piece_jointe')}
            hasArrow
          >
            <IconButton
              aria-label={t('back_office.conversations.ajouter_piece_jointe')}
              icon={<AttachmentIcon />}
              variant='outline'
              color='gray.dark'
              onClick={() => {
                documentInputRef.current?.click();
              }}
              isDisabled={!selectedContactId || isSubmitting}
            />
          </Tooltip>
          <Tooltip
            label={t('back_office.conversations.ajouter_image')}
            hasArrow
          >
            <IconButton
              aria-label={t('back_office.conversations.ajouter_image')}
              icon={<ImageIcon />}
              variant='outline'
              color='gray.dark'
              onClick={() => {
                imageInputRef.current?.click();
              }}
              isDisabled={!selectedContactId || isSubmitting}
            />
          </Tooltip>
        </HStack>
      </FormControl>
    </VStack>
  );

  return (
    <ModaleLayout
      visible={modal.visible}
      hide={modal.remove}
      titre={t('back_office.conversations.nouvelle_conversation')}
      footer={
        <BlueButton
          texte={t('back_office.conversations.commencer_conversation')}
          onClick={() => {
            if (selectedContactId) {
              const formData = new FormData();
              formData.append('contenu', messageContent.trim());
              formData.append('destinataire_contact_id', selectedContactId);
              for (const file of selectedFiles) {
                formData.append('pieces_jointes', file);
              }
              submitInitierConversation(formData);
            }
          }}
          isDisabled={!isFormValid}
          isLoading={isSubmitting}
          w='100%'
        />
      }
    >
      <Box>{body}</Box>
    </ModaleLayout>
  );
});
